/**
 * PDF Download Utility
 * Opens print dialog using unified PDF template (same approach as invoices)
 */

import { generatePdfHtml, PdfTemplateConfig, DEFAULT_PDF_TEMPLATE_CONFIG } from '../components/pdf/unified-pdf-template';
import { getDisplayNotes } from './estimate-notes';

export interface EstimatePdfData {
  id?: number;
  estimateNumber?: string;
  invoiceNumber?: string;
  issueDate?: string | Date;
  expiryDate?: string | Date;
  dueDate?: string | Date;
  status?: string;
  title?: string;
  description?: string;
  subtotal?: number | string;
  tax?: number | string;
  discount?: number | string;
  total?: number | string;
  client?: any;
  contractor?: any;
  items?: any[];
  notes?: string;
  terms?: string;
  clientSignature?: string;
}

export interface InvoicePdfData {
  id?: number;
  invoiceNumber?: string;
  issueDate?: string | Date;
  dueDate?: string | Date;
  status?: string;
  title?: string;
  description?: string;
  subtotal?: number | string;
  tax?: number | string;
  discount?: number | string;
  total?: number | string;
  amountPaid?: number | string;
  client?: any;
  contractor?: any;
  items?: any[];
  notes?: string;
  terms?: string;
  clientSignature?: string;
}

/**
 * Opens print dialog for estimate PDF (same approach as invoices)
 */
export function downloadEstimatePdf(
  data: EstimatePdfData,
  config?: PdfTemplateConfig | null,
  filename?: string
): void {
  try {
    const clientSignature =
      (data as any).clientSignature || (data as any).client_signature || null;

    // Normalize data to handle old estimates with missing fields
    const normalizedData = {
      ...data,
      notes: getDisplayNotes(data.notes),
      items: data.items || [],
      client: data.client || {},
      contractor: data.contractor || {},
      estimateNumber: data.estimateNumber || data.estimate_number || `EST-${data.id || 'UNKNOWN'}`,
      total: data.total || 0,
      subtotal: data.subtotal || 0,
      tax: data.tax || 0,
      discount: data.discount || 0,
      description: data.description || '',
      title: data.title || 'Estimate',
      clientSignature,
      client_signature: clientSignature,
    };

    const status = String((normalizedData as any).status || '').toLowerCase();
    const isSigned =
      !!clientSignature && (status === 'accepted' || status === 'signed' || status === 'approved' || status === 'converted');

    // Generate HTML using unified template with normalized data
    const htmlContent = generatePdfHtml({
      data: normalizedData,
      config: config ? { ...DEFAULT_PDF_TEMPLATE_CONFIG, ...config } : DEFAULT_PDF_TEMPLATE_CONFIG,
      type: 'estimate',
      isSigned
    });

    // Open new window and write HTML (same approach as invoices)
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      throw new Error('Popup blocked. Please allow popups to download PDF.');
    }

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Wait for content to load, then trigger print dialog (user can Save as PDF)
    setTimeout(() => {
      printWindow.print();
    }, 250);
  } catch (error) {
    console.error('Error downloading estimate PDF:', error);
    throw error;
  }
}

/**
 * Opens print dialog for invoice PDF (same approach as invoices)
 */
export function downloadInvoicePdf(
  data: InvoicePdfData,
  config?: PdfTemplateConfig | null,
  filename?: string
): void {
  try {
    // Normalize data to handle old invoices with missing fields
    const normalizedData = {
      ...data,
      items: data.items || [],
      client: data.client || {},
      contractor: data.contractor || {},
      invoiceNumber: data.invoiceNumber || data.invoice_number || `INV-${data.id || 'UNKNOWN'}`,
      total: data.total || 0,
      subtotal: data.subtotal || 0,
      tax: data.tax || 0,
      discount: data.discount || 0,
      description: data.description || '',
      title: data.title || 'Invoice'
    };

    // Generate HTML using unified template with normalized data
    const htmlContent = generatePdfHtml({
      data: normalizedData,
      config: config ? { ...DEFAULT_PDF_TEMPLATE_CONFIG, ...config } : DEFAULT_PDF_TEMPLATE_CONFIG,
      type: 'invoice',
      isSigned: !!data.clientSignature
    });

    // Open new window and write HTML (same approach as invoices)
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      throw new Error('Popup blocked. Please allow popups to download PDF.');
    }

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Wait for content to load, then trigger print dialog (user can Save as PDF)
    setTimeout(() => {
      printWindow.print();
    }, 250);
  } catch (error) {
    console.error('Error downloading invoice PDF:', error);
    throw error;
  }
}
